import {
  StorageConsentDataType,
  FeatureFlagVariationType,
  ForcedExperimentVariationDataType,
  ForcedExperimentVariationType,
  ForcedFeatureVariationDataType,
  IExternalStorage,
  VariationDataType,
} from 'src/storage/types';
import { KameleoonLogger } from 'src/logging';
import { StorageDataType } from './types';
import { KameleoonStorageKey } from 'src/storage/constants';

export class StorageCleanupManager<T extends StorageDataType> {
  private readonly cleanupInterval: number;
  private readonly externalStorage: IExternalStorage<T>;

  constructor(externalStorage: IExternalStorage<T>, cleanupInterval: number) {
    this.externalStorage = externalStorage;
    this.cleanupInterval = cleanupInterval;

    if (cleanupInterval > 0) {
      this.initCleanupInterval();
    }
  }

  private initCleanupInterval(): void {
    for (const storageKey of Object.values(KameleoonStorageKey)) {
      let cleanupInterval: NodeJS.Timeout | null = null;
      try {
        switch (storageKey) {
          case KameleoonStorageKey.ForcedFeatureVariation:
          case KameleoonStorageKey.ForcedExperimentVariation:
          case KameleoonStorageKey.VariationData:
          case KameleoonStorageKey.ConsentData:
            KameleoonLogger.info`Starting cleanup for ${storageKey} with interval ${this.cleanupInterval}`;
            cleanupInterval = setInterval(
              () => this.cleanupData(storageKey),
              this.cleanupInterval,
            );
            break;
          case KameleoonStorageKey.ClientData:
          case KameleoonStorageKey.TargetingData:
          case KameleoonStorageKey.TrackingData:
          case KameleoonStorageKey.VisitorCode:
          case KameleoonStorageKey.DataInfo:
          case KameleoonStorageKey.KameleoonSimulationFFData:
            break;
          default:
            // check if all cases are handled
            const exhaustiveCheck: never = storageKey;
            KameleoonLogger.error`Unknown storage key: ${storageKey} for initCleanupInterval.`;
        }
      } catch (Error) {
        if (cleanupInterval) {
          clearInterval(cleanupInterval);
        }
        KameleoonLogger.error`Error while starting cleanup for ${storageKey} with interval ${this.cleanupInterval}`;
      }
    }
  }

  public cleanupData(storageKey: KameleoonStorageKey): void {
    const dataResult: any = this.externalStorage.read(storageKey);

    if (!dataResult) {
      return;
    }
    const currentTime = Date.now();

    switch (storageKey) {
      case KameleoonStorageKey.VariationData:
        this.cleanupVariationData(dataResult, currentTime);
        break;
      case KameleoonStorageKey.ForcedFeatureVariation:
        this.cleanupForcedFeatureVariationData(dataResult, currentTime);
        break;
      case KameleoonStorageKey.ForcedExperimentVariation:
        this.cleanupForcedExperimentVariationData(dataResult, currentTime);
        break;
      case KameleoonStorageKey.ConsentData:
        this.cleanupConsentData(dataResult, currentTime);
        break;
      case KameleoonStorageKey.ClientData:
      case KameleoonStorageKey.TargetingData:
      case KameleoonStorageKey.TrackingData:
      case KameleoonStorageKey.VisitorCode:
      case KameleoonStorageKey.DataInfo:
      case KameleoonStorageKey.KameleoonSimulationFFData:
        break;
      default:
        // check if all cases are handled
        const exhaustiveCheck: never = storageKey;
        KameleoonLogger.error`Unknown storage key: ${storageKey} for cleanupData.`;
    }

    this.externalStorage.write(storageKey, dataResult);
  }

  private cleanupConsentData(
    consentData: StorageConsentDataType,
    currentTime: number,
  ): void {
    for (const [visitorCode, consent] of Object.entries(consentData)) {
      // for consistency with the old data in the storage
      if (typeof consent === 'boolean') {
        consentData[visitorCode] = {
          consent,
          expirationTime: currentTime + this.cleanupInterval,
        } as {
          consent: boolean;
          expirationTime: number;
        };
        continue;
      }
      const value = consent as {
        consent: boolean;
        expirationTime?: number;
      };
      if (typeof value.expirationTime === 'number') {
        if (value.expirationTime < currentTime) {
          delete consentData[visitorCode];
        }
      } else {
        value.expirationTime = currentTime + this.cleanupInterval;
      }
    }
  }

  private cleanupVariationData(
    variationData: VariationDataType,
    currentTime: number,
  ): void {
    for (const [visitorCode, featureVariations] of Object.entries(
      variationData,
    )) {
      this.cleanupObjectValues(
        featureVariations as {
          [key: number]: FeatureFlagVariationType & { expirationTime?: number };
        },
        currentTime,
      );

      if (Object.keys(variationData[visitorCode]).length === 0) {
        delete variationData[visitorCode];
      }
    }
  }

  private cleanupForcedFeatureVariationData(
    forcedFeatureVariationData: ForcedFeatureVariationDataType,
    currentTime: number,
  ): void {
    for (const [simulated, visitorData] of Object.entries(
      forcedFeatureVariationData,
    )) {
      for (const [visitorCode, features] of Object.entries(visitorData)) {
        this.cleanupObjectValues(
          features as {
            [key: string]: ForcedFeatureVariationDataType & {
              expirationTime?: number;
            };
          },
          currentTime,
        );

        if (
          Object.keys(
            forcedFeatureVariationData[Number(simulated)][visitorCode],
          ).length === 0
        ) {
          delete forcedFeatureVariationData[Number(simulated)][visitorCode];
        }
      }
    }
  }

  private cleanupForcedExperimentVariationData(
    forcedExperimentVariationData: ForcedExperimentVariationDataType,
    currentTime: number,
  ): void {
    for (const [visitorCode, experiments] of Object.entries(
      forcedExperimentVariationData,
    )) {
      this.cleanupObjectValues(
        experiments as {
          [key: number]: ForcedExperimentVariationType & {
            expirationTime?: number;
          };
        },
        currentTime,
      );

      if (
        Object.keys(forcedExperimentVariationData[visitorCode]).length === 0
      ) {
        delete forcedExperimentVariationData[visitorCode];
      }
    }
  }

  private cleanupObjectValues(
    obj: {
      [key: string | number]: { expirationTime?: number };
    },
    currentTime: number,
  ): void {
    for (const [key, value] of Object.entries(obj)) {
      if (typeof value.expirationTime === 'number') {
        if (value.expirationTime < currentTime) {
          delete obj[key];
        }
      } else {
        value.expirationTime = currentTime + this.cleanupInterval;
      }
    }
  }
}
